import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Divider, Grid } from '@mui/material';
import {
  ProductTransaction,
  ProductTransactionParentType,
  ProductTransactionType,
  productTransactionTypeToCustomFieldEntitySubType,
} from '../../../../../../types/productTransaction';
import { ProductMasterDataContext } from '../../../../../../context/ProductMasterDataContext';
import { LotContext } from '../../../../../../context/LotContext';
import { BinContext } from '../../../../../../context/BinContext';
import { ProductContext } from '../../../../../../context/ProductContext';
import { HandlingUnitContext } from '../../../../../../context/HandlingUnitContext';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ProductTransactionContext } from '../../../../../../context/ProductTransactionContext';
import { StockLocationRoleAssignmentContext } from '../../../../../../context/StockLocationRoleAssignmentContext';
import { CustomFieldContext } from '../../../../../../context/CustomFieldContext';
import { CustomFieldEntityType } from '../../../../../../types/custom_field/customField';
import { StockLocationContext } from '../../../../../../context/StockLocationContext';
import { ContainerContext } from '../../../../../../context/ContainerContext';
import { FlexPane } from '../../../../../../VentoryUI/components/common/FlexPane/FlexPane';
import Table from '../../../../../components/Table';
import { getAllChildProductTransactions } from '../../../../../../util/containers.util';
import { productTransactionTableHeaders } from '../../../../Common/ProductTransactionTable';
import { ContainerTypeContext } from '../../../../../../context/ContainerTypeContext';
import { UserContext } from '../../../../../../context/UserContext';
import { CompanyContext } from '../../../../../../context/CompanyContext';
import { StockLocationRole } from '../../../../../../types/stockLocationRoleAssignment';
import ProcessOrderTransactionHeader from './Views/ProcessOrderTransactionHeader';
import ProcessOrderTransactionBody from './Views/ProcessOrderTransactionBody';
import LoadingPackage from '../../../../Common/LoadingPackage';
import { Company } from '../../../../../../types/company';
import { TableSettingsContext } from '../../../../../../context/TableSettingsContext';
import { EntityType } from '../../../../../../types/comment';
import { OrderContext } from '../../../../../../context/OrderContext';
import ProcessProductTransactionFooter from './Views/Common/ProcessProductTransactionFooter';

interface ProcessOrderTransactionInfoPaneProps {
  input: ProductTransaction;
  setError: (error: string) => void;
  onClose: () => void;
  disabled?: boolean;
  parentInfo?: boolean;
}

export default function ProcessOrderTransactionInfoPane({
  input,
  setError,
  onClose,
  disabled,
  parentInfo = false,
}: ProcessOrderTransactionInfoPaneProps) {
  dayjs.extend(customParseFormat);
  const { productMasterData } = useContext(ProductMasterDataContext);
  const { lots } = useContext(LotContext);
  const { bins, binsLoading } = useContext(BinContext);
  const { containers, setContainers, containersLoading } = useContext(ContainerContext);
  const { containerTypes } = useContext(ContainerTypeContext);
  const { products } = useContext(ProductContext);
  const { handlingUnits } = useContext(HandlingUnitContext);
  const { productTransactions, setProductTransactions, productTransactionsLoading } =
    useContext(ProductTransactionContext);
  const { hasStockLocationRole } = useContext(StockLocationRoleAssignmentContext);
  const { customFields } = useContext(CustomFieldContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { companyUsers, currentUser, locale } = useContext(UserContext);
  const { currentCompany } = useContext(CompanyContext);
  const { getTableSettings } = useContext(TableSettingsContext);
  const { orders, setOrders } = useContext(OrderContext);

  const productTransactionTableSettings = getTableSettings(
    EntityType.productTransaction,
    currentCompany.id,
    EntityType.order,
  );

  const [transaction, setTransaction] = useState<ProductTransaction>(new ProductTransaction(input));
  const [userId, setUserId] = useState<string | undefined>();

  const pmd = productMasterData.get(transaction.product.pmdId || '');
  const container = containers.get(transaction.product.containerId || '');
  if (!pmd && !container) return null;

  const containerTransaction = productTransactions.get(transaction.containerTransactionId || 'NONE');

  const scanRequired = Company.hasScanRequired(currentCompany, currentUser || undefined);

  useEffect(() => {
    if (containerTransaction?.product.toBinId) {
      setTransaction(transaction => transaction.withToBinId(containerTransaction.product.toBinId, true));
    }
  }, []);

  const relevantCustomFields = useMemo(() => {
    const productTransactionEntityTypes = new Set<CustomFieldEntityType>([CustomFieldEntityType.productTransaction]);
    if (transaction.product.containerId) productTransactionEntityTypes.add(CustomFieldEntityType.containerTransaction);

    return [...customFields.values()].filter(
      cf =>
        (!cf.generated &&
          !cf.hidden &&
          productTransactionEntityTypes.has(cf.entityType) &&
          cf.entitySubtype === productTransactionTypeToCustomFieldEntitySubType(transaction.type)) ||
        cf.entityType === CustomFieldEntityType.product,
    );
  }, [customFields, transaction.product.containerId, transaction.type]);

  const stockLocationId =
    transaction.type === ProductTransactionType.inbound || transaction.type === ProductTransactionType.replenish
      ? transaction.product.toStockLocationId
      : transaction.product.fromStockLocationId;
  if (!stockLocationId) return null;

  disabled ||=
    !hasStockLocationRole(transaction.companyId, stockLocationId, StockLocationRole.STOCK_LOCATION_USER) ||
    binsLoading ||
    containersLoading;

  const isLoading = binsLoading || containersLoading;
  return (
    <FlexPane
      header={
        <Grid container className='px-6 pb-2 pt-6'>
          <ProcessOrderTransactionHeader
            transaction={transaction}
            stockLocationId={stockLocationId}
            container={container}
            productMasterData={pmd}
            showParentInfo={parentInfo}
            scanRequired={scanRequired}
            disabled={disabled}
          />
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>
          {isLoading ? (
            <Grid container justifyContent={'center'} alignContent={'center'}>
              <LoadingPackage />
            </Grid>
          ) : (
            <ProcessOrderTransactionBody
              transaction={transaction}
              setTransaction={setTransaction}
              container={container}
              productMasterData={pmd}
              userId={userId}
              setUserId={setUserId}
              disabled={disabled || scanRequired}
            />
          )}
          <Grid item xs={12}>
            <ProcessProductTransactionFooter input={transaction} handleClose={onClose} />
          </Grid>
        </Grid>
      }
      content={
        transaction.product.containerId ? (
          <Table<ProductTransaction>
            modal
            customPagination={5}
            title={'Container Contents'}
            items={getAllChildProductTransactions(transaction, productTransactions).filter(
              t => t.parentType !== ProductTransactionParentType.transaction,
            )}
            headers={productTransactionTableHeaders(
              productTransactionTableSettings,
              stockLocations,
              bins,
              productMasterData,
              lots,
              companyUsers,
              containers,
              containerTypes,
              locale,
            )}
          />
        ) : null
      }
    />
  );
}
